/**
 * Subscription plan banner.
 *
 * This component displays the information of the subscription plan of the user
 * in a banner placed in top of the main screen.
 *
 * Copyright © Ircam Amplify 2024-2026
 */

import {
  AccountUtil,
  CompanyConsumption,
  TextUtil,
  TierType,
  Util,
} from '@amplify3/duplo-back-end'
import { BiCoin } from 'react-icons/bi'
import { Link } from 'react-router-dom'
import { getLayoutState, getUserState, useSelector } from '../store'
import './SubscriptionPlanBanner.css'

const SubscriptionPlanBanner = () => {
  const { userInfo, companyConsumption } = useSelector(getUserState)
  const { sideBarCollapsed } = useSelector(getLayoutState)

  if (!userInfo || !companyConsumption?.subscription_tier) return <div></div>

  /**
   * Return the plan name to display in the banner message.
   *
   * @param tier The tier.
   * @returns A string containing the name, or 'Unknown plan'
   *  if the tier is undefined.
   */
  function getPlanName(
    tier: string | undefined,
    subscription_name: string | undefined
  ): string {
    return (
      TextUtil.capitalizeWordFirstChar(
        AccountUtil.humanReadableTier(tier, subscription_name)
      ) + ' plan'
    )
  }

  /**
   * The component displaying the message with the name of the plan.
   */
  const PlanMessageComp = () => {
    return (
      <div>
        {companyConsumption?.subscription_tier !== TierType.Free && (
          <div className="planMessage">
            You are using the{' '}
            <span className="planMessage-name">
              {getPlanName(
                companyConsumption?.subscription_tier,
                companyConsumption?.subscription_name
              )}
            </span>
          </div>
        )}
      </div>
    )
  }

  /**
   * Component displaying the counter of remaining tracks the user is able
   * to process, with prefix and suffix messages
   * depending on the tier.
   */
  const TrackCounter = ({
    consumption,
  }: {
    consumption: CompanyConsumption
  }) => {
    const consumedCount = AccountUtil.getConsumption(consumption)
    const trackCount = Util.absRound(
      TierType.Free == consumption?.subscription_tier
        ? consumedCount + AccountUtil.getPaidCreditCount(companyConsumption)
        : consumedCount
    )

    // The unit
    const unit = trackCount !== 0 && trackCount !== 1 ? ' tracks ' : ' track '

    // The suffix
    const getCreditSuffix = () => {
      switch (consumption?.subscription_tier) {
        case TierType.Free:
        case TierType.TopUpCreditAimd:
          return 'remaining'
        case TierType.Subscription:
          return (
            `/ ${consumption.current_plan} this ` +
            (consumption.current_subscription_frequency === 'monthly'
              ? 'month'
              : 'year')
          )
        case TierType.PayAsYouGo:
          return 'this month'
        default:
          return null
      }
    }
    const suffix = getCreditSuffix()

    return (
      <div className="creditCounter">
        <BiCoin size={25} className="creditCounter-icon" />
        {trackCount} {unit}
        {suffix}
      </div>
    )
  }

  interface ConsumptionInfoProps {
    consumption: CompanyConsumption
  }

  /**
   * Component displaying the information of the consumption, with link to
   * change the plan.
   */
  const ConsumptionInfoComp = ({ consumption }: ConsumptionInfoProps) => {
    const getLinkLabel = () => {
      switch (consumption?.subscription_tier) {
        case TierType.Free:
          return 'Upgrade now'
        case TierType.TopUpCreditAimd:
          return 'Buy tracks'
        case TierType.Subscription:
        case TierType.PayAsYouGo:
          return 'Browse plan'
        default:
          return null
      }
    }
    const linkLabel = getLinkLabel()

    const getPrefixMsg = () => {
      if (
        TierType.Free != consumption?.subscription_tier &&
        TierType.TopUpCreditAimd != consumption?.subscription_tier
      ) {
        return 'You have used'
      }

      return null
    }
    const prefixMsg = getPrefixMsg()

    return (
      <div className="consumptionInfo">
        {prefixMsg}

        <TrackCounter consumption={consumption} />

        {linkLabel && (
          <Link to="/tierselection" className="consumptionInfo-link">
            {linkLabel}
          </Link>
        )}
      </div>
    )
  }

  return (
    <div
      className={`subscriptionPlan${
        !sideBarCollapsed ? ' sidebar-expanded' : ''
      }`}
    >
      <PlanMessageComp />
      {AccountUtil.hasCreditLimit(companyConsumption) && (
        <ConsumptionInfoComp consumption={companyConsumption} />
      )}
    </div>
  )
}

export default SubscriptionPlanBanner
